import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Monitor, Smartphone, AlertTriangle, Gauge } from 'lucide-react';
import Badge from './Badge';
import LighthouseActionText from './LighthouseActionText';
import LighthouseAffectedElements from './LighthouseAffectedElements';
import {
  getRecommendations,
  getPriority,
  getMetricPriority,
  buildVitalContextualRecommendation,
} from '../utils/performanceRecommendations';
import { translateLighthouse, effortInfo, AXE_LABELS, isLighthouseMetricAudit } from '../utils/auditDictionary';

const AXE_ORDER = ['performance', 'accessibilite', 'seo', 'technique', 'mobile', 'social'];

/** Indique si une ligne d’audit a du contenu à montrer au dépliage. */
function findingHasDetailContent(rec, t) {
  if (!t || t?.kind === 'metric' || isLighthouseMetricAudit(rec.id)) return false;
  const d = rec.detailsInfo;
  return Boolean(
    t.pourquoi
    || t.corriger
    || t.descriptionHtml
    || rec.description
    || rec.displayValue
    || d?.codeSnippets?.length
    || d?.files?.length
    || d?.urls?.length
    || d?.warnings?.length
    || d?.sizes?.length
  );
}

function CompactFindingDetail({ rec, t }) {
  const details = rec.detailsInfo;
  const hasAction = Boolean(
    (t.known && t.corriger)
    || (!t.known && (t.descriptionHtml || rec.description)),
  );
  const hasAffected = Boolean(
    details?.codeSnippets?.length
    || details?.files?.length
    || details?.urls?.length,
  );

  if (!t.pourquoi && !hasAction && !hasAffected && !rec.displayValue) {
    return (
      <p className="text-xs text-blue-gray-500 italic">
        Détail non disponible dans le rapport exporté.
      </p>
    );
  }

  return (
    <>
      {rec.displayValue && (
        <p className="text-xs font-semibold text-blue-gray-800 tabular-nums">{rec.displayValue}</p>
      )}
      {t.pourquoi && (
        <p className="text-xs text-blue-gray-600 leading-relaxed">
          <span className="font-medium text-blue-gray-700">Impact : </span>
          {t.pourquoi}
        </p>
      )}
      <LighthouseActionText
        translation={t}
        rawDescription={rec.description}
        className="!text-xs !mt-0"
      />
      <LighthouseAffectedElements detailsInfo={details} maxItems={5} />
    </>
  );
}

function MetricVitalCard({ rec, allRecommendations, expanded, onToggle }) {
  const t = translateLighthouse(rec);
  if (!t) return null;
  const prio = getMetricPriority(rec.id, rec.numericValue);
  const eff = effortInfo(t.effort);
  const contextual = useMemo(
    () => buildVitalContextualRecommendation(rec, allRecommendations),
    [rec, allRecommendations],
  );
  const isOpen = expanded.has(rec.id);
  const recCount = contextual.lines.length;
  const borderBg =
    prio.color === 'error'
      ? 'border-red-200 bg-red-50/50'
      : prio.color === 'warning'
        ? 'border-amber-200 bg-amber-50/50'
        : 'border-green-200 bg-green-50/50';

  return (
    <div className={`rounded-xl border overflow-hidden ${borderBg}`}>
      <button
        type="button"
        onClick={() => onToggle(rec.id)}
        className="w-full text-left px-4 py-3 flex items-start gap-2 hover:bg-white/40 transition-colors"
        aria-expanded={isOpen}
      >
        <div className="flex-1 min-w-0">
          <span className="text-sm font-semibold text-blue-gray-900 leading-snug block pr-1">
            {t.label}
          </span>
          {!isOpen && recCount > 0 && (
            <span className="text-xs text-blue-gray-500 mt-1 block">
              {recCount} recommandation{recCount > 1 ? 's' : ''} — cliquer pour le détail
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rec.displayValue && (
            <span className="text-lg font-bold text-blue-gray-900 tabular-nums leading-none">
              {rec.displayValue}
            </span>
          )}
          <Badge color={prio.color} size="sm">{prio.label}</Badge>
          <ChevronDown
            size={16}
            className={`text-blue-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="px-4 pb-4 pt-0 border-t border-neutral-200/80 bg-white/50">
          <div className="pt-3 space-y-2">
            {(t.definition || t.pourquoi) && (
              <p className="text-sm text-blue-gray-600 leading-relaxed">
                {t.definition || t.pourquoi}
              </p>
            )}
            {t.impactWhy && (
              <p className="text-sm text-blue-gray-800 leading-relaxed font-medium border-l-2 border-amber-400/80 pl-2.5">
                {t.impactWhy}
              </p>
            )}
            {rec.displayValue && (
              <p className="text-xs font-semibold text-blue-gray-800 tabular-nums">
                Mesure actuelle : {rec.displayValue}
              </p>
            )}
          </div>
          <div className="mt-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-gray-500 mb-2">
              Recommandations sur cette page
            </p>
            <ul className="space-y-2.5">
              {contextual.lines.map((line, i) => (
                <li
                  key={i}
                  className="text-sm text-blue-gray-800 leading-relaxed pl-3 border-l-2 border-accent/40"
                >
                  {line}
                </li>
              ))}
            </ul>
          </div>
          <span className="inline-block mt-3 text-xs text-blue-gray-400" title={eff.hint}>
            Effort estimé : {eff.label}
          </span>
        </div>
      )}
    </div>
  );
}

const PRIO_DOT = {
  error: 'bg-red-500',
  warning: 'bg-amber-400',
  info: 'bg-blue-400',
  success: 'bg-green-500',
  secondary: 'bg-blue-gray-300',
};

function CompactFindingRow({ rec, expanded, onToggle }) {
  const t = translateLighthouse(rec);
  if (!t) return null;
  const prio = getPriority(rec.score);
  const canExpand = findingHasDetailContent(rec, t);
  const isOpen = expanded.has(rec.id);

  return (
    <div className="border-b border-neutral-100 last:border-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (canExpand) onToggle(rec.id);
        }}
        className={`w-full text-left py-2.5 px-1 flex items-center gap-2 min-w-0 ${canExpand ? 'cursor-pointer hover:bg-blue-gray-50/60' : 'cursor-default'}`}
        disabled={!canExpand}
        aria-expanded={canExpand ? isOpen : undefined}
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${PRIO_DOT[prio.color] || PRIO_DOT.secondary}`} />
        <span className="text-sm text-blue-gray-800 truncate flex-1">{t.label}</span>
        {!isOpen && rec.displayValue && (
          <span className="text-[10px] text-blue-gray-400 shrink-0 tabular-nums">{rec.displayValue}</span>
        )}
        <span className="text-[10px] text-blue-gray-400 shrink-0 hidden sm:inline">{prio.label}</span>
        {canExpand && (
          <ChevronDown
            size={14}
            className={`text-blue-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>
      {isOpen && canExpand && (
        <div className="pb-3 pl-4 pr-2 space-y-2 border-l-2 border-accent/25 ml-2 mb-1">
          <CompactFindingDetail rec={rec} t={t} />
        </div>
      )}
    </div>
  );
}

function FindingCard({ rec, expanded, onToggle }) {
  const t = translateLighthouse(rec);
  if (!t) return null;
  const prio = getPriority(rec.score);
  const eff = effortInfo(t.effort);
  const details = rec.detailsInfo;
  const canExpand = findingHasDetailContent(rec, t);
  const isOpen = expanded.has(rec.id);

  return (
    <div className="border border-blue-gray-200 rounded-lg overflow-hidden hover:border-accent/30 transition-colors">
      <div
        className={`flex items-start gap-3 p-4 ${canExpand ? 'cursor-pointer hover:bg-blue-gray-50/50' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          if (canExpand) onToggle(rec.id);
        }}
        role={canExpand ? 'button' : undefined}
        aria-expanded={canExpand ? isOpen : undefined}
      >
        <div
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
            prio.color === 'error'
              ? 'bg-red-100 text-red-600'
              : prio.color === 'warning'
                ? 'bg-amber-100 text-amber-600'
                : 'bg-green-100 text-green-600'
          }`}
        >
          {rec.score !== null && rec.score !== undefined ? Math.round(rec.score * 100) : '—'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-medium text-blue-gray-900 text-sm">{t.label}</span>
            <Badge color={prio.color} size="sm">{prio.label}</Badge>
            <span
              className="text-xs text-blue-gray-500 bg-blue-gray-50 px-1.5 py-0.5 rounded font-medium"
              title={eff.hint}
            >
              {eff.label}
            </span>
            {rec.deviceScope && rec.deviceScope !== 'both' && (
              <span className="text-xs text-blue-gray-400 flex items-center gap-0.5">
                {rec.deviceScope === 'mobile' ? <Smartphone size={11} /> : <Monitor size={11} />}
                {rec.deviceScope}
              </span>
            )}
          </div>
          {!isOpen && t.pourquoi && (
            <p className="text-xs text-blue-gray-500 line-clamp-2">{t.pourquoi}</p>
          )}
        </div>
        {canExpand && (
          <div className="flex-shrink-0 mt-1 text-blue-gray-400">
            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </div>
        )}
      </div>

      {isOpen && canExpand && (
        <div className="px-4 pb-4 pt-0 border-t border-blue-gray-100 bg-blue-gray-50/20">
          <div className="pt-3 space-y-3">
            {t.pourquoi && (
              <p className="text-sm text-blue-gray-600">
                <span className="font-medium text-blue-gray-700">Impact : </span>
                {t.pourquoi}
              </p>
            )}
            <LighthouseActionText translation={t} rawDescription={rec.description} />
            {rec.displayValue && (
              <div className="text-xs font-mono bg-white border border-blue-gray-200 px-2 py-1 rounded inline-block text-blue-gray-700">
                {rec.displayValue}
              </div>
            )}
            <LighthouseAffectedElements detailsInfo={details} maxItems={15} />
            {details?.sizes?.length > 0 && (() => {
              const totalWasted = details.sizes.reduce((sum, s) => sum + (s.wasted || 0), 0);
              if (totalWasted <= 0) return null;
              return (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <AlertTriangle size={14} className="text-amber-600 flex-shrink-0" />
                  <span className="text-xs text-amber-800">
                    {totalWasted > 1024 * 1024
                      ? `${(totalWasted / 1024 / 1024).toFixed(1)} MB`
                      : `${(totalWasted / 1024).toFixed(0)} KB`}{' '}
                    de données pourraient être économisées
                  </span>
                </div>
              );
            })()}
            {details?.warnings?.length > 0 && (
              <div className="space-y-1">
                {details.warnings.map((w, wi) => (
                  <div key={wi} className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5">
                    {w}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Liste complète des retours Lighthouse / PageSpeed interprétés pour une page.
 */
export default function LighthouseFindingList({
  pagespeedRaw,
  groupByAxe = true,
  className = '',
  initialLimit = null,
  showVitals = true,
  compact = false,
  /** true = pas de titre de section (déjà couvert par le parent, ex. « Détail PageSpeed ») */
  embedded = false,
}) {
  const recommendations = useMemo(
    () => getRecommendations(pagespeedRaw),
    [pagespeedRaw],
  );
  const [expanded, setExpanded] = useState(() => new Set());
  const [vitalExpanded, setVitalExpanded] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);

  const { vitals, actions } = useMemo(() => {
    const v = [];
    const a = [];
    recommendations.forEach((rec, index) => {
      const item = { rec, index };
      if (isLighthouseMetricAudit(rec.id)) v.push(item);
      else a.push(item);
    });
    return { vitals: v, actions: a };
  }, [recommendations]);

  useEffect(() => {
    setShowAll(false);
    setExpanded(new Set());
    setVitalExpanded(new Set());
  }, [pagespeedRaw]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleVital = (id) => {
    setVitalExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const limit = initialLimit != null && initialLimit > 0 ? initialLimit : null;
  const visibleActions = useMemo(() => {
    if (!limit || showAll) return actions;
    return actions.slice(0, limit);
  }, [actions, limit, showAll]);
  const hiddenCount = limit && !showAll ? Math.max(0, actions.length - limit) : 0;

  const visibleGrouped = useMemo(() => {
    if (!groupByAxe) {
      return [{ axe: null, items: visibleActions }];
    }
    const map = new Map();
    visibleActions.forEach(({ rec, index }) => {
      const t = translateLighthouse(rec);
      const axe = t?.axe || 'performance';
      if (!map.has(axe)) map.set(axe, []);
      map.get(axe).push({ rec, index });
    });
    const ordered = AXE_ORDER.filter((a) => map.has(a));
    for (const [axe] of map) {
      if (!ordered.includes(axe)) ordered.push(axe);
    }
    return ordered.map((axe) => ({ axe, items: map.get(axe) }));
  }, [visibleActions, groupByAxe]);

  if (recommendations.length === 0) {
    return (
      <p className={`text-sm text-blue-gray-500 ${className}`}>
        Aucun point d&apos;amélioration PageSpeed détecté sur cette page (ou audit non disponible).
      </p>
    );
  }

  const CardComponent = compact ? CompactFindingRow : FindingCard;
  const listSpacing = compact ? 'divide-y divide-neutral-100 rounded-md border border-neutral-100' : 'space-y-3';

  return (
    <div className={`${compact ? 'space-y-3' : 'space-y-5'} ${className}`}>
      {showVitals && vitals.length > 0 && (
        <div>
          <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-blue-gray-500 mb-2">
            <Gauge size={12} />
            Vitesse ressentie par le visiteur
          </h4>
          <p className="text-sm text-blue-gray-500 mb-3 leading-relaxed">
            Indicateurs de vitesse perçue (Google PageSpeed). Dépliez une carte pour voir
            l&apos;explication et les recommandations liées aux audits en échec sur cette page.
          </p>
          <div className="flex flex-col gap-2">
            {vitals.map(({ rec }) => (
              <MetricVitalCard
                key={rec.id}
                rec={rec}
                allRecommendations={recommendations}
                expanded={vitalExpanded}
                onToggle={toggleVital}
              />
            ))}
          </div>
        </div>
      )}

      {visibleActions.length > 0 && (
        <div className={showVitals && vitals.length > 0 ? 'mt-4' : ''}>
          {!embedded && (
            <h4 className="text-xs font-semibold uppercase tracking-wide text-blue-gray-500 mb-1">
              Retours du diagnostic PageSpeed
              {hiddenCount > 0
                ? ` (${visibleActions.length} sur ${actions.length})`
                : ` (${actions.length})`}
            </h4>
          )}
          <p className={`text-sm text-blue-gray-500 leading-relaxed ${embedded ? 'mb-2' : 'mb-3'}`}>
            {hiddenCount > 0 ? (
              <>
                Google signale <strong className="font-medium text-blue-gray-700">{actions.length} vérifications</strong>
                {' '}sur cette page ; les <strong className="font-medium text-blue-gray-700">{visibleActions.length} premières</strong>
                {' '}sont les plus impactantes. Dépliez une ligne pour l&apos;explication et les éléments concernés
                (fichiers, scripts, sélecteurs…).
              </>
            ) : (
              <>
                <strong className="font-medium text-blue-gray-700">{actions.length} vérification{actions.length > 1 ? 's' : ''}</strong>
                {' '}signalée{actions.length > 1 ? 's' : ''} par Google PageSpeed sur cette page (performance, accessibilité,
                bonnes pratiques…). Dépliez une ligne pour le détail.
              </>
            )}
          </p>
          {visibleGrouped.map(({ axe, items }) => (
            <div key={axe || 'all'} className={embedded ? '' : showVitals && vitals.length > 0 ? 'mt-2' : ''}>
              {groupByAxe && axe && (
                <h4 className="text-xs font-semibold uppercase tracking-wide text-blue-gray-500 mb-2">
                  {AXE_LABELS[axe] || axe} ({items.length})
                </h4>
              )}
              <div className={listSpacing}>
                {items.map(({ rec, index }) => (
                  <CardComponent
                    key={rec.id || index}
                    rec={rec}
                    expanded={expanded}
                    onToggle={toggle}
                  />
                ))}
              </div>
            </div>
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="mt-3 w-full text-sm font-medium text-accent hover:text-accent/80 border border-accent/25 rounded-lg py-2.5 bg-accent/5 transition"
            >
              Voir les {hiddenCount} autre{hiddenCount > 1 ? 's' : ''} vérification{hiddenCount > 1 ? 's' : ''} PageSpeed
            </button>
          )}
        </div>
      )}
    </div>
  );
}
